import type { Ref } from 'vue'
import { queryCollectionSearchSections } from '#imports'
import { AcademicCapIcon, DocumentTextIcon } from '@heroicons/vue/24/outline'
import { hash } from 'ohash'
import { fetchAcademyNav, hiddenLessonPaths } from '../utils/nav'

interface SearchData {
  id: string
  title: string
  content: string
  level: number
  titles: string[]
}

interface AcademyNavItem {
  path: string
  icon?: string
  title: string
  children?: AcademyNavChild[]
}

interface AcademyNavChild {
  path: string
  title: string
  children?: AcademyNavLeaf[]
}

interface AcademyNavLeaf {
  path: string
}

export function useAcademySearch(searchTerm: Ref<string>) {
  const searchData = ref<SearchData[]>([])
  const academyNav = ref<AcademyNavItem[]>([])

  onMounted(async () => {
    const [academyNavResult, searchDataResult] = await Promise.all([
      fetchAcademyNav(),
      queryCollectionSearchSections('pages'),
    ])

    // We use a simplified type here because the original type is recursive and cause issues with Vue's ref
    academyNav.value = academyNavResult.data.value as AcademyNavItem[]
    searchData.value = searchDataResult.filter(item => !hiddenLessonPaths.value.has(item.id.split('#')[0]!))
  })

  const navGroups = computed(() => {
    return academyNav.value.map(item => ({
      key: item.path,
      label: `Forgd Academy > ${item.title}`,
      to: item.path,
      commands: (item.children || []).map(child => ({
        id: hash(child),
        icon: AcademicCapIcon,
        label: child.title,
        to: child.children?.[0]?.path || child.path,
      })),
    }))
  })

  const filterGroups = computed(() => {
    return navGroups.value.map((group) => {
      const parentTitle = group.label.split('>')[1]?.trim() || ''
      return {
        ...group,
        commands: searchData.value
          .filter(item => item.id.startsWith(group.to as string))
          .map(item => ({
            ...item,
            id: hash(item),
            path: item.id,
            to: item.id,
            icon: item.level <= 1 ? AcademicCapIcon : DocumentTextIcon,
            label: `${parentTitle} > ${item.title}`,
          })),
      }
    })
  })

  return computed(() => {
    return searchTerm.value.trim().length > 0 ? filterGroups.value : navGroups.value
  })
}
